import { ReactNode, Suspense, SyntheticEvent, useState } from 'react';

import { TFormalOfferVariationType } from '@helpers/FormalOffers/formalOffers.types';
import { getDurationInMonths } from '@helpers/GetDurationInMonths/getDurationInMonths';
import { FormattedCurrency } from '@iwIntlProvider/FormattedCurrency';
import { IconInfoCircleOutline } from '@iwoca/icons';
import { Button, Modal } from '@iwoca/orion';
import { Accordion } from '@iwoca/orion-ui';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';

import styles from './OfferVariationModal.module.css';
import { useStateKey } from '../../../../iwQueryClient/useStateKey';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { CalculatorIcon } from '../../../ui/icons/CalculatorIcon';
import { getInterestOnlySchedule } from '../../MyOffers.helpers';
import { DrawdownRepaymentSchedule, FormalOffer } from '../../MyOffers.types';
import { getMaxDate } from '../earlyRepaymentCalculator/EarlyRepaymentHelper';
import { InlineEarlyRepaymentCalculator } from '../inlineEarlyRepaymentCalculator/InlineEarlyRepaymentCalculator';

export const OfferVariationModal = ({
    isOpen,
    onClose,
    ...props
}: {
    isOpen: boolean;
    onClose: () => void;
    drawdownAmount: number;
    baseOffer: FormalOffer;
    variedOffer: FormalOffer;
    variationType: TFormalOfferVariationType;
    handleSubmit?: (props: { selectedOffer: FormalOffer }) => void;
}) => {
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            closeIcon
            className={styles.modal}
        >
            <Suspense fallback={<MainContentPending />}>
                <OfferVariationModalContent {...props} />
            </Suspense>
        </Modal>
    );
};

const OfferVariationModalContent = ({
    variationType,
    ...props
}: {
    drawdownAmount: number;
    baseOffer: FormalOffer;
    variedOffer: FormalOffer;
    variationType: TFormalOfferVariationType;
    handleSubmit?: (props: { selectedOffer: FormalOffer }) => void;
}) => {
    switch (variationType) {
        case 'IO':
            return <InterestOnlyVariationModal {...props} />;
    }
};

function useRepaymentSchedule({
    offer,
    drawdownAmount
}: {
    offer: FormalOffer;
    drawdownAmount: number;
}) {
    const stateKey = useStateKey();
    const { data: drawdownSchedule } = useQuery({
        queryKey: [
            'draw_down_repayment_schedule',
            stateKey,
            drawdownAmount,
            offer.offer_id,
            offer.product_type
        ],
        queryFn: async () => {
            // eslint-disable-next-line iwoca/prefer-lapi-client
            const response = await fetch(
                `/api/lending/edge/draw_down_repayment_schedule/${stateKey}/${offer.product_type}/${offer.offer_id}/?amount=${drawdownAmount}`
            );

            return (await response.json()) as {
                data: DrawdownRepaymentSchedule;
            };
        },
        suspense: true
    });
    return drawdownSchedule!.data;
}

function runLengths<T>(
    arr: T[],
    getValue: (arg: T) => number
): { value: number; count: number }[] {
    return _.reduce(
        arr,
        (result, item) => {
            const last = _.last(result);
            if (last && last.value === getValue(item)) {
                last.count += 1;
            } else {
                result.push({ value: getValue(item), count: 1 });
            }
            return result;
        },
        [] as { value: number; count: number }[]
    );
}

const InterestOnlyVariationModal = ({
    drawdownAmount,
    baseOffer,
    variedOffer,
    handleSubmit
}: {
    drawdownAmount: number;
    baseOffer: FormalOffer;
    variedOffer: FormalOffer;
    handleSubmit?: (props: { selectedOffer: FormalOffer }) => void;
}) => {
    const baseOfferRepaymentSchedule = useRepaymentSchedule({
        offer: baseOffer,
        drawdownAmount
    });
    const variedOfferRepaymentSchedule = useRepaymentSchedule({
        offer: variedOffer,
        drawdownAmount
    });

    const ioSchedule =
        getInterestOnlySchedule(variedOfferRepaymentSchedule.schedule) ?? [];

    const ioRepaymentRunLengths = runLengths(
        ioSchedule,
        (repayment) => repayment.total
    );

    const remainingRepaymentRunLengths = runLengths(
        baseOfferRepaymentSchedule.schedule,
        (repayment) => repayment.total
    );

    return (
        <>
            <div
                className={classNames(
                    'max-h-[70vh] overflow-auto px-xl pt-l',
                    handleSubmit ? undefined : 'pb-xl'
                )}
                style={{
                    scrollbarGutter: 'stable both-edges'
                }}
            >
                <div className="mb-s text-2xl font-bold max-xs:text-l">
                    <FormattedMessage
                        id="_.interest_only.popup.heading"
                        defaultMessage="Add {interestOnlyRepayments, plural, one {# month} other {# months}} of lower repayments"
                        values={{
                            interestOnlyRepayments:
                                variedOffer.interest_only_repayments
                        }}
                    />
                </div>
                <div className="font-reg max-xs:text-xs">
                    <FormattedMessage
                        id="_.interest_only.popup.description"
                        defaultMessage="You can add a {interestOnlyRepayments} month period to the start of your loan, where you will only be repaying interest, which will be bolted on to your existing {baseDuration} month term."
                        values={{
                            interestOnlyRepayments:
                                variedOffer.interest_only_repayments,
                            baseDuration: getDurationInMonths(
                                baseOffer.duration!
                            )
                        }}
                    />
                </div>
                <RepaymentAccordion
                    offer={variedOffer}
                    drawdownAmount={drawdownAmount}
                />
                <div className="pt-l max-xs:pt-m">
                    <RepaymentBreakdown
                        runLengths={ioRepaymentRunLengths}
                        circleColours={[
                            '#fff1f0',
                            'var(--color-core-95)',
                            'var(--color-core-90)'
                        ]}
                        borderColour="var(--color-core-90)"
                        pill={{
                            colour: 'var(--color-core-95)',
                            content: (
                                <FormattedMessage
                                    id="_.interest_only.popup.pill"
                                    defaultMessage="This would be added to the loan"
                                />
                            )
                        }}
                        description={
                            <FormattedMessage
                                id="_.interest_only.popup.io_period_description"
                                defaultMessage="You'll only pay us interest throughout this period"
                            />
                        }
                    />
                    <RepaymentBreakdown
                        runLengths={remainingRepaymentRunLengths}
                        isFirst={false}
                        circleColours={[
                            'var(--color-secondary-95)',
                            'var(--color-secondary-90)',
                            'var(--color-secondary-80)'
                        ]}
                        description={
                            <FormattedMessage
                                id="_.interest_only.popup.following_period_description"
                                defaultMessage="You'll reduce your balance each month, just like with our standard {duration}-month loan"
                                values={{
                                    duration: getDurationInMonths(
                                        baseOffer.duration!
                                    )
                                }}
                            />
                        }
                    />
                </div>
                <div className="mt-xl rounded-s bg-primary-10 px-xl py-l text-s text-structure-100 max-xs:mt-l max-xs:p-m max-xs:pt-s">
                    <table className="w-[100%] border-separate border-spacing-x-l border-spacing-y-s max-xs:border-spacing-x-xs">
                        <tbody>
                            <tr>
                                <td className="max-xs:text-xs">
                                    <FormattedMessage
                                        id="_.interest_only.popup.full_term_label"
                                        defaultMessage="Full term:"
                                    />
                                </td>
                                <td className="text-m font-bold max-xs:text-xs">
                                    <FormattedMessage
                                        id="_.interest_only.popup.full_term_value"
                                        defaultMessage="{duration, plural, one {# month} other {# months}}"
                                        values={{
                                            duration: getDurationInMonths(
                                                variedOffer.duration!
                                            )
                                        }}
                                    />
                                </td>
                                <td className="text-core-90 max-xs:text-end max-xs:text-xs">
                                    {
                                        <FormattedMessage
                                            id="_.interest_only.popup.full_term_tip"
                                            defaultMessage="+ {ioDuration, plural, one {# month} other {# months}} vs {baseDuration, plural, one {# month} other {# months}}"
                                            values={{
                                                ioDuration:
                                                    variedOffer.interest_only_repayments!,
                                                baseDuration:
                                                    getDurationInMonths(
                                                        baseOffer.duration!
                                                    )
                                            }}
                                        />
                                    }
                                </td>
                            </tr>
                            <tr>
                                <td className="max-xs:text-xs">
                                    <span className="max-xs:hidden">
                                        <FormattedMessage
                                            id="_.interest_only.popup.total_repayment_label"
                                            defaultMessage="Total repayment:"
                                        />
                                    </span>
                                    <span className="hidden max-xs:inline">
                                        <FormattedMessage
                                            id="_.interest_only.popup.total_repayment_label_small"
                                            defaultMessage="Total:"
                                        />
                                    </span>
                                </td>
                                <td className="text-m font-bold max-xs:text-xs">
                                    <FormattedCurrency
                                        value={
                                            variedOfferRepaymentSchedule.summary
                                                .total_payable!
                                        }
                                    />
                                </td>
                                <td className="text-core-90 max-xs:text-end max-xs:text-xs">
                                    <FormattedMessage
                                        id="_.interest_only.popup.full_cost_tip"
                                        defaultMessage="+ {extra, number, ::currency/GBP} vs {baseDuration, plural, one {# month} other {# months}}"
                                        values={{
                                            extra: _.sumBy(ioSchedule, 'total'),
                                            baseDuration: getDurationInMonths(
                                                baseOffer.duration!
                                            )
                                        }}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
            {handleSubmit && (
                <div className="grid border-t border-secondary-90 shadow-black-m">
                    <div className="m-xl grid grid-cols-2 gap-l max-xs:hidden">
                        <Button
                            variant="tertiary"
                            onClick={() =>
                                handleSubmit({ selectedOffer: baseOffer })
                            }
                        >
                            <FormattedMessage
                                id="_.interest_only.popup.no"
                                defaultMessage="No, I'm ok for now"
                            />
                        </Button>
                        <Button
                            onClick={() =>
                                handleSubmit({ selectedOffer: variedOffer })
                            }
                        >
                            <FormattedMessage
                                id="_.interest_only.popup.yes"
                                defaultMessage="Yes, add to my loan"
                            />
                        </Button>
                    </div>
                    <div className="m-l hidden flex-col gap-l max-xs:flex">
                        <Button
                            onClick={() =>
                                handleSubmit({ selectedOffer: variedOffer })
                            }
                        >
                            <FormattedMessage
                                id="_.interest_only.popup.yes"
                                defaultMessage="Yes, add to my loan"
                            />
                        </Button>
                        <Button
                            variant="tertiary"
                            onClick={() =>
                                handleSubmit({ selectedOffer: baseOffer })
                            }
                        >
                            <FormattedMessage
                                id="_.interest_only.popup.no"
                                defaultMessage="No, I'm ok for now"
                            />
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};

const RepaymentBreakdown = ({
    runLengths,
    circleColours,
    isFirst = true,
    borderColour = 'transparent',
    pill,
    description
}: {
    runLengths: { value: number; count: number }[];
    circleColours: string[];
    isFirst?: boolean;
    borderColour?: string;
    pill?: {
        colour: string;
        content: ReactNode | null;
    };
    description: ReactNode;
}) => {
    return (
        <div
            className="relative rounded-s border-2 border-dashed p-l max-xs:p-s max-xs:pt-m"
            style={{ borderColor: borderColour }}
        >
            {pill && (
                <div
                    className="absolute -top-m left-0 rounded-xl px-m py-xs text-xs"
                    style={{ backgroundColor: pill.colour }}
                >
                    {pill.content}
                </div>
            )}
            {runLengths.map((run, i) => (
                <div
                    key={i}
                    className="flex flex-row gap-s py-s text-l font-bold max-xs:text-s"
                >
                    <OverlappingCircles
                        className="max-xs:hidden"
                        circleCount={run.count}
                        colours={circleColours}
                    />
                    {i === 0 && isFirst ? (
                        run.count === 1 ? (
                            <FormattedMessage
                                id="_.interest_only.popup.first_repayment_schedule_one_month"
                                defaultMessage="{price, number, ::currency/GBP} for first month"
                                values={{
                                    price: run.value
                                }}
                            />
                        ) : (
                            <FormattedMessage
                                id="_.interest_only.popup.first_repayment_schedule"
                                defaultMessage="{price, number, ::currency/GBP} /month for first {remainingRepayments, plural, one {# month} other {# months}}"
                                values={{
                                    price: run.value,
                                    remainingRepayments: run.count
                                }}
                            />
                        )
                    ) : run.count === 1 ? (
                        <FormattedMessage
                            id="_.interest_only.popup.following_repayment_schedule_one_month"
                            defaultMessage="{price, number, ::currency/GBP} for following month"
                            values={{
                                price: run.value
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="_.interest_only.popup.following_repayment_schedule"
                            defaultMessage="{price, number, ::currency/GBP} /month for following {interestOnlyRepayments, plural, one {# month} other {# months}}"
                            values={{
                                price: run.value,
                                interestOnlyRepayments: run.count
                            }}
                        />
                    )}
                </div>
            ))}
            <div className="text-xs font-reg">{description}</div>
        </div>
    );
};

const OverlappingCircles = ({
    className,
    circleCount,
    size = '21px',
    colours
}: {
    className?: string;
    circleCount: number;
    size?: string;
    colours: string[];
}) => {
    return (
        <div
            className={classNames('ml-s flex flex-row items-center', className)}
        >
            {colours.map((colour, i) => (
                <div
                    key={i}
                    className="-ml-s rounded-full border-solid bg-structure-100"
                    style={{
                        width: size,
                        height: size,
                        borderColor:
                            i < colours.length - circleCount
                                ? 'var(--color-structure-100)'
                                : colour,
                        borderWidth: '3px'
                    }}
                ></div>
            ))}
        </div>
    );
};

const RepaymentAccordion = ({
    drawdownAmount,
    offer
}: {
    drawdownAmount: number;
    offer: FormalOffer;
}) => {
    const [isOpen, setOpen] = useState(false);
    return (
        <Accordion className="mb-xl mt-l max-xs:mb-l">
            <Accordion.Item
                open={isOpen}
                {...{
                    // Orion UI doesn't recognise this prop, but it does pass it down to the underlying `<details>` component
                    onToggle: (
                        e: SyntheticEvent<HTMLDetailsElement, ToggleEvent>
                    ) => setOpen(e.nativeEvent.newState === 'open')
                }}
                variant="secondary"
                size="small"
            >
                <Accordion.Summary
                    className={styles.accordionSummary}
                    rightSection={<></>}
                >
                    <div className="flex w-[100%] flex-row flex-wrap justify-between gap-s">
                        <span>
                            <IconInfoCircleOutline
                                className="mr-s inline"
                                height="20"
                                width="20"
                            />
                            <span className="mr-auto">
                                <FormattedMessage
                                    id="_.interest_only.popup.no_early_repayment_fees"
                                    defaultMessage="<b>Remember</b>, no fees for repaying early."
                                    values={{
                                        b: (children) => (
                                            <span className="font-bold">
                                                {children}
                                            </span>
                                        )
                                    }}
                                />
                            </span>
                        </span>
                        <span className="text-s font-bold text-primary-40">
                            {isOpen ? (
                                <FormattedMessage
                                    id="_.interest_only.popup.hide_repayment_calculator_label"
                                    defaultMessage="Hide calculator"
                                />
                            ) : (
                                <FormattedMessage
                                    id="_.interest_only.popup.show_repayment_calculator_label"
                                    defaultMessage="Calculate potential savings"
                                />
                            )}
                            <CalculatorIcon className="ml-xs inline h-[20px] w-[20px] fill-primary-40" />
                        </span>
                    </div>
                </Accordion.Summary>
                <Accordion.Content className="border-b-0 border-l-0 border-r-0 border-t-[1px] border-secondary-80">
                    <InlineEarlyRepaymentCalculator
                        drawdownAmount={drawdownAmount}
                        offerId={offer.offer_id!}
                        loanDuration={getDurationInMonths(offer.duration!)}
                        maxDate={getMaxDate(offer.duration!.amount)}
                        productType={offer.product_type!}
                        variant="just-subheading"
                        showOnMobile
                        infoBoxClassName="!bg-structure-100"
                    />
                </Accordion.Content>
            </Accordion.Item>
        </Accordion>
    );
};
